import { Action, createReducer, on } from '@ngrx/store';
import { AuthState } from '@shared/store/auth/auth.state';
import {
  authError,
  checkLoginMail,
  checkLoginMailFailure,
  checkLoginMailSuccess,
  checkManualLoginCode,
  getAuthPagesBranding,
  getAuthPagesBrandingFailure,
  getAuthPagesBrandingSuccess,
  getListOfSignInCompanies,
  getListOfSignInCompaniesFailure,
  getListOfSignInCompaniesSuccess,
  hideNewCodeSent,
  loginFailure,
  loginSuccess,
  loginWithToken,
  loginWithTokenSuccess,
  newSignInEmailSent,
  pendingSignInSuccess,
  sendVerificationCode,
  sendVerificationCodeFailure,
  sendVerificationCodeSuccess,
  setPendingSignIn,
  setRedirectUrlAfterLoginAction,
  setSignInLinkCompanies,
} from './auth.action';

const authInitialState: AuthState = {
  errorCode: null,
  email: null,
  lastSignInCode: null,
  lastTimeVerificationCodeSent: null,
  isNewCodeSent: false,
  signInCompanyId: null,
  redirectUrl: null,
  doesEmailExist: null,
  signInCompanies: [],
  branding: null,
  pendingSignIn: null,
  loadingActions: {
    isSendingVerificationCode: false,
    isCheckingLoginEmail: false,
    isLoadingBranding: false,
    isLoadingSignInCompanies: false,
    isLoginFromParamsLoading: false,
  },
  errors: null,
  loading: false,
};

const reducer = createReducer(
  authInitialState,

  on(
    checkLoginMail,
    (state, { email }): AuthState => ({
      ...state,
      email,
      loadingActions: {
        ...state.loadingActions,
        isCheckingLoginEmail: true,
      },
    }),
  ),

  on(
    loginWithToken,
    (state, { redirectUrl }): AuthState => ({
      ...state,
      redirectUrl,
      loadingActions: {
        ...state.loadingActions,
        isLoginFromParamsLoading: true,
      },
    }),
  ),

  on(
    setSignInLinkCompanies,
    (state, { email, code, signInCompanyId, redirectUrl, companies }): AuthState => ({
      ...state,
      email,
      signInCompanyId,
      redirectUrl: redirectUrl ?? state.redirectUrl,
      doesEmailExist: null,
      lastSignInCode: code,
      errorCode: null,
      signInCompanies: companies,
    }),
  ),

  on(
    checkManualLoginCode,
    (state, { verificationCode }): AuthState => ({
      ...state,
      lastSignInCode: verificationCode,
      isNewCodeSent: false,
    }),
  ),

  on(
    hideNewCodeSent,
    (state): AuthState => ({
      ...state,
      isNewCodeSent: false,
    }),
  ),

  on(
    setPendingSignIn,
    (state, { pendingSignIn }): AuthState => ({
      ...state,
      pendingSignIn,
    }),
  ),

  on(
    pendingSignInSuccess,
    (state): AuthState => ({
      ...state,
      pendingSignIn: null,
    }),
  ),

  on(
    loginWithTokenSuccess,
    (state): AuthState => ({
      ...state,
      loading: false,
      errors: null,
      loadingActions: {
        ...state.loadingActions,
        isLoginFromParamsLoading: false,
      },
    }),
  ),

  on(
    checkLoginMailFailure,
    (state, { error }): AuthState => ({
      ...state,
      loadingActions: {
        ...state.loadingActions,
        isCheckingLoginEmail: false,
        isLoginFromParamsLoading: false,
      },
      errors: error,
    }),
  ),

  on(
    loginFailure,
    (state, { error }): AuthState => ({
      ...state,
      loading: false,
      errors: error,
      loadingActions: {
        ...state.loadingActions,
        isLoginFromParamsLoading: false,
      },
    }),
  ),

  on(
    checkLoginMailSuccess,
    (state): AuthState => ({
      ...state,
      loadingActions: {
        ...state.loadingActions,
        isCheckingLoginEmail: false,
      },
    }),
  ),

  on(
    loginSuccess,
    (state, { link }): AuthState => ({
      ...state,
      loading: false,
      redirectUrl: link ?? state.redirectUrl,
      loadingActions: {
        ...state.loadingActions,
        isLoginFromParamsLoading: false,
      },
    }),
  ),

  on(
    authError,
    (state, { errorCode }): AuthState => ({
      ...state,
      errorCode,
    }),
  ),

  on(
    checkLoginMail,
    (state, { email }): AuthState => ({
      ...state,
      email,
      doesEmailExist: null,
      isNewCodeSent: false,
      signInCompanyId: null,
    }),
  ),

  on(
    newSignInEmailSent,
    (state, { email, signInCompanyId }): AuthState => ({
      ...state,
      email,
      signInCompanyId,
      doesEmailExist: null,
      errorCode: null,
      lastSignInCode: null,
      lastTimeVerificationCodeSent: new Date(),
      isNewCodeSent: true,
    }),
  ),

  on(
    checkLoginMailSuccess,
    (state, { doesEmailExist }): AuthState => ({
      ...state,
      doesEmailExist: doesEmailExist ?? false,
      lastSignInCode: null,
    }),
  ),

  on(
    sendVerificationCode,
    (state): AuthState => ({
      ...state,
      errorCode: null,
      lastSignInCode: null,
      loadingActions: {
        ...state.loadingActions,
        isSendingVerificationCode: true,
      },
    }),
  ),

  on(
    sendVerificationCodeSuccess,
    (state, { isResend }): AuthState => ({
      ...state,
      lastSignInCode: null,
      lastTimeVerificationCodeSent: new Date(),
      isNewCodeSent: isResend,
      loadingActions: {
        ...state.loadingActions,
        isSendingVerificationCode: false,
      },
    }),
  ),

  on(
    sendVerificationCodeFailure,
    (state, { error }): AuthState => ({
      ...state,
      loadingActions: {
        ...state.loadingActions,
        isSendingVerificationCode: false,
      },
      errors: error,
    }),
  ),

  on(
    setRedirectUrlAfterLoginAction,
    (state, { redirectUrl }): AuthState => ({
      ...state,
      redirectUrl,
    }),
  ),

  on(
    getAuthPagesBranding,
    (state): AuthState => ({
      ...state,
      loadingActions: {
        ...state.loadingActions,
        isLoadingBranding: true,
      },
    }),
  ),

  on(
    getAuthPagesBrandingSuccess,
    (state, { branding }): AuthState => ({
      ...state,
      branding,
      loadingActions: {
        ...state.loadingActions,
        isLoadingBranding: false,
      },
    }),
  ),

  on(
    getAuthPagesBrandingFailure,
    (state, { error }): AuthState => ({
      ...state,
      loadingActions: {
        ...state.loadingActions,
        isLoadingBranding: false,
      },
      errors: error,
    }),
  ),

  on(
    getListOfSignInCompanies,
    (state): AuthState => ({
      ...state,
      loadingActions: {
        ...state.loadingActions,
        isLoadingSignInCompanies: true,
      },
    }),
  ),

  on(
    getListOfSignInCompaniesSuccess,
    (state, { companies }): AuthState => ({
      ...state,
      errorCode: null,
      signInCompanies: companies,
      loadingActions: {
        ...state.loadingActions,
        isLoadingSignInCompanies: false,
      },
    }),
  ),

  on(
    getListOfSignInCompaniesFailure,
    (state, { error }): AuthState => ({
      ...state,
      loadingActions: {
        ...state.loadingActions,
        isLoadingSignInCompanies: false,
      },
      errors: error,
    }),
  ),
);

export const authReducer = (state: AuthState = authInitialState, action: Action): AuthState => reducer(state, action);
